import dayjs from 'dayjs';
import localData from 'dayjs/plugin/localeData';
import prepareTranslate from './dictionary';
import { isObjectEmpty } from './is';

const WHITE_LIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const SPLIT_CHAR = '.';

/**
 * create random string
 * @param {number} length result string length
 * @returns {string} random string
 */
const generateRandomString = (length = 10) => {
  let result = '';
  const characters = WHITE_LIST;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

/**
 * replace string inside another string in specific position
 * @param {object} parameters
 * @returns {string}
 */
const replaceLetters = (parameters) => {
  const {
    string = '',
    from = 0,
    to = 0,
    replacement = '',
  } = parameters;

  return string.substring(0, from) + replacement + string.substring(to);
};

/**
 * add commas to number
 * @param {number} number
 * @returns {string} number with commas
 */
const addCommas = (number) => {
  const num = (typeof number === 'number') ? number : convertStringToNumber(number);
  return num.toLocaleString('en-US');
};

/**
 * limit number digits
 * @param {number} number
 * @param {number} digits
 * @returns {number}
 */
const limitNumberDigits = (number, digits = 3) => {
  const num = (typeof number === 'number') ? number : convertStringToFloat(number);
  return num.toPrecision(digits + 1);
};

/**
 * remove all special chars from string and leave only letters and numbers
 * @param {string} path
 * @returns {string}
 */
const getCleanString = (path = '') => String(path).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

/**
 * add slash to path if not exists as first char
 * @param {string} path
 * @returns {string}
 */
const addSlashToPath = (path = '') => ((String(path).charAt(0) === '/') ? path : `/${path}`);

/**
 * convert string to number if can, returns back val if cant convert
 * @param {mixed} val
 * @returns {mixed}
 */
const convertStringToNumber = (val = '') => (
  (typeof val === 'string' && !Number.isNaN(parseInt(val, 10)))
    ? parseInt(val, 10)
    : val
);

/**
 * convert string to float if can, returns back val if cant convert
 * @param {mixed} val
 * @returns {mixed}
 */
const convertStringToFloat = (val = '') => (
  (typeof val === 'string' && !Number.isNaN(parseInt(val, 10)))
    ? parseFloat(val, 10)
    : val
);

/**
 * slugify text
 * @param {string} str
 * @returns {string}
 */
const slugify = (str = '') => String(str)
  .normalize('NFKD') // split accented characters into their base characters and diacritical marks
  .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
  .trim() // trim leading or trailing whitespace
  .toLowerCase() // convert to lowercase
  .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
  .replace(/\s+/g, '-') // replace spaces with hyphens
  .replace(/-+/g, '-'); // remove consecutive hyphens

/**
 * format date into string
 * @param {mixed} d date value. object or string
 * @param {boolean} withTime includes time in result
 * @returns {string}
 */
const formatDateToString = (d = '', withTime = false) => {
  if (!d) return '';

  dayjs.extend(localData);

  const t = prepareTranslate();
  const months = dayjs.months();
  const monthName = String(months[dayjs(d).month()]).toLocaleLowerCase();
  const monthNameTranslated = t(`global.months.${monthName}`) || monthName;
  let format = `DD ${t('global.in')}${monthNameTranslated} YYYY`;

  if (withTime) {
    format += ', HH:mm';
  }

  const formatted = dayjs(d, 'D/M/YYYY').format(format);

  return formatted;
};

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * format dd/mm/yyyy date into string
 * @param {mixed} d string date value
 * @param {boolean} withTime includes time in result
 * @returns {string}
 */
const formatDDMMYYYYToString = (d = '', withTime = false) => {
  if (!d) return '';

  dayjs.extend(localData);

  const t = prepareTranslate();
  const months = dayjs.months();

  // Split the date string and create a new Date object
  const [day, month, year] = d.split('/');
  const parsedDate = dayjs(new Date(year, month - 1, day));

  if (!parsedDate.isValid()) {
    return 'Invalid Date';
  }

  const monthName = String(months[parsedDate.month()]).toLowerCase();

  const monthNameTranslated = t(`global.months.${monthName}`) || monthName;
  let format = `DD ${t('global.in')}${monthNameTranslated} YYYY`;

  if (withTime) {
    format += `, ${t('global.in')}${t('global.hour')} HH:mm`;
  }

  const formatted = parsedDate.format(format);

  return formatted;
};

const FAMILY_STATUS = Object.freeze({
  single: 'רווק/ה',
  married: 'נשוי/אה',
  divorced: 'גרוש/ה',
  widowed: 'אלמן/ה',
});

const GENDER = Object.freeze({
  male: 'זכר',
  female: 'נקבה',
});

const AGENT_TYPE = Object.freeze({
  CTO: 'מנכ"ל',
  houseAgent: 'סוכן בית',
  integratedAgent: 'סוכן משולב',
  foreignAgent: 'סוכן חוץ',
});

/**
 * truncate string and put dots in the middle (ellipsis)
 * @param {string} str source string
 * @param {number} firstCharCount number of characters to be shown in the first portion
 * @param {*} endCharCount number of characters to be shown in the end portion
 * @param {*} dotCount count of dots to be placed between the first and end portions
 * @param {string} replaceChar character to replace with
 * @returns {string}
 */
const truncateString = (str = '', firstCharCount = str.length, endCharCount = 0, dotCount = 3, replaceChar = '.') => {
  // No truncation needed
  if (str.length <= firstCharCount + endCharCount) {
    return str;
  }

  const firstPortion = str.slice(0, firstCharCount);
  const endPortion = str.slice(-endCharCount);
  const dots = replaceChar.repeat(dotCount);

  return `${firstPortion}${dots}${endPortion}`;
};

/**
 * check if date value is after now
 * @param {mixed} d date value. object or string
 * @returns {boolean}
 */
const isDateAfterNow = (d) => ((d) ? dayjs(d).isAfter(dayjs()) : false);

/**
 * get value from object using string dot notation. example: 'details.name.first'
 * @param {object} data
 * @param {string} str - dot notation string
 * @returns {mixed}
 */
const getItemValue = (data, str) => {
  const source = String(str).trim();
  const keys = source.split(SPLIT_CHAR);

  const traverse = (current, remainingKeys) => {
    if (remainingKeys.length === 0) return current;

    const [key, ...rest] = remainingKeys;

    if (Array.isArray(current)) {
      // Traverse each item in the array and collect results
      return current.flatMap((item) => {
        const result = traverse(item[key], rest);
        return Array.isArray(result) ? result : [result];
      });
    }
    if (current && typeof current === 'object') {
      return traverse(current[key], rest);
    }

    return undefined;
  };

  try {
    return traverse(data, keys);
  } catch {
    return null;
  }
};

/**
 * change query string into object
 * @param {string} queryString
 * @returns {object}
 */
const queryStringToObject = (queryString = '') => JSON.parse(queryString);

/**
 * change object into query string
 * @param {object} params
 * @param {string} prefix
 * @returns {string}
 */
const objectToQueryString = (params = {}) => {
  if (isObjectEmpty(params)) {
    return '';
  }
  return JSON.stringify(params);
};

const formatDateToDDMMYYYY = (inputDate) => {
  // Parse the input date string
  const dateObj = new Date(inputDate);

  // Extract day, month, and year
  const day = dateObj.getDate();
  const month = dateObj.getMonth() + 1; // Month is zero-based
  const year = dateObj.getFullYear();

  // Add leading zeros if needed
  const formattedDay = day < 10 ? `0${day}` : day;
  const formattedMonth = month < 10 ? `0${month}` : month;

  // Create the dd/mm/yyyy format
  const formattedDate = `${formattedDay}/${formattedMonth}/${year}`;

  return formattedDate;
};

const interpolateTemplate = (template, values) => {
  if (typeof template !== 'string') return '';

  return template.replace(/{{(.*?)}}/g, (_, keyPath) => {
    const keys = keyPath.trim().split('.');
    const value = keys.reduce((acc, key) => acc?.[key], values);
    return value ?? '';
  });
};

/**
 * Format month and year into localized string (e.g., "ינואר 2025")
 * @param {number} month - Month number (1-12)
 * @param {number} year - Year number (e.g., 2025)
 * @returns {string} Formatted month and year
 */
const formatMonthYearString = (month, year) => {
  if (!month || !year) return '';
  dayjs.extend(localData);
  const t = prepareTranslate();
  const adjustedMonth = parseInt(month, 10) - 1;
  const months = dayjs.months();
  const monthName = String(months[adjustedMonth]).toLowerCase();
  const monthNameTranslated = t(`global.months.${monthName}`) || monthName;
  return `${monthNameTranslated} ${year}`;
};

const stripHtmlTags = (htmlString) => {
  if (!htmlString) return '';
  return htmlString.replace(/<\/?[^>]+(>|$)/g, '');
};

const shortenText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/**
 * Splits a filename into its base name and extension
 *
 * @param {string} filename - The full filename to split (e.g., "document.pdf")
 * @returns {Object} An object containing the base name and extension
 * @returns {string} baseName - The base name of the file without extension
 * @returns {string} extension - The file extension including the dot
 * @throws {Error} Will throw an error if filename is not a string or has no extension
 * @example
 * // Returns { baseName: "document", extension: ".pdf" }
 * splitFileName("document.pdf");
 */
const splitFileName = (filename) => {
  const lastDotIndex = filename.lastIndexOf('.');
  return {
    baseName: filename.slice(0, lastDotIndex),
    extension: filename.slice(lastDotIndex),
  };
};

/**
 * Joins a base name and extension to form a complete filename
 *
 * @param {string} baseName - The base name of the file without extension
 * @param {string} extension - The file extension (should include the dot)
 * @returns {string} The complete filename with extension
 * @example
 * // Returns "document.pdf"
 * joinFileName("document", ".pdf");
 */
const joinFileName = (baseName, extension) => `${baseName}${extension}`;

export {
  generateRandomString,
  replaceLetters,
  addCommas,
  getCleanString,
  addSlashToPath,
  convertStringToNumber,
  limitNumberDigits,
  convertStringToFloat,
  slugify,
  formatDateToString,
  formatDDMMYYYYToString,
  truncateString,
  isDateAfterNow,
  getItemValue,
  FAMILY_STATUS,
  GENDER,
  AGENT_TYPE,
  queryStringToObject,
  objectToQueryString,
  formatDateToDDMMYYYY,
  formatDate,
  interpolateTemplate,
  formatMonthYearString,
  stripHtmlTags,
  shortenText,
  splitFileName,
  joinFileName,
};
